import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import AdminService from '../../../services/adminService';
import {
  TOTAL_WIZARD_STEPS,
  WIZARD_STEP_FALLBACK_LABELS,
  WIZARD_STEP_KEYS
} from '../../../constants/wizardSteps';

/**
 * Wizard Exit Manager Component
 *
 * Manages wizard exit tracking and user intent analysis:
 * - View wizard sessions and exit patterns
 * - Configure exit intents and follow-up actions
 * - Track user journey abandonment points
 * - Generate reports on user behavior
 */
const WizardExitManager = () => {
  const { t } = useLanguage();
  const [sessions, setSessions] = useState([]);
  const [exitIntents, setExitIntents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('sessions');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('7days');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sessionsData, intentsData] = await Promise.all([
        AdminService.getWizardSessions(),
        AdminService.getWizardExitIntents()
      ]);
      setSessions(sessionsData || []);
      setExitIntents(intentsData || []);
    } catch (error) {
      console.error('Error loading wizard data:', error);
      // Fallback to mock data if Supabase fails
      setSessions(mockSessions);
      setExitIntents(mockExitIntents);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockSessions = [
    {
      id: '1',
      session_id: 'sess_001',
      start_time: '2025-10-05T10:30:00Z',
      end_time: '2025-10-05T10:45:00Z',
      current_step: 3,
      total_steps: TOTAL_WIZARD_STEPS,
      exit_reason: 'Too expensive',
      exit_intent: 'too_expensive',
      contact_email: 'user@example.com',
      follow_up_status: 'pending',
      country_selection: { selectedCountry: 'canada' }
    },
    {
      id: '2',
      session_id: 'sess_002',
      start_time: '2025-10-05T09:15:00Z',
      end_time: '2025-10-05T09:20:00Z',
      current_step: 1,
      total_steps: TOTAL_WIZARD_STEPS,
      exit_reason: 'Just browsing',
      exit_intent: 'just_browsing',
      contact_email: null,
      follow_up_status: 'none',
      country_selection: null
    },
    {
      id: '3',
      session_id: 'sess_003',
      start_time: '2025-10-04T16:45:00Z',
      end_time: '2025-10-04T17:30:00Z',
      current_step: 7,
      total_steps: TOTAL_WIZARD_STEPS,
      exit_reason: 'Need more information',
      exit_intent: 'need_more_info',
      contact_email: 'interested@example.com',
      follow_up_status: 'contacted',
      country_selection: { selectedCountry: 'germany' }
    }
  ];

  const mockExitIntents = [
    {
      id: '1',
      intent_key: 'too_expensive',
      intent_label: 'Too Expensive',
      description: 'User finds the costs too high for their budget',
      followup_required: true,
      session_count: 15,
      conversion_rate: 0.2
    },
    {
      id: '2',
      intent_key: 'need_more_info',
      intent_label: 'Need More Information',
      description: 'User needs additional details about services',
      followup_required: true,
      session_count: 12,
      conversion_rate: 0.5
    },
    {
      id: '3',
      intent_key: 'just_browsing',
      intent_label: 'Just Browsing',
      description: 'User was exploring without serious intent',
      followup_required: false,
      session_count: 8,
      conversion_rate: 0.1
    }
  ];

  const getStepName = (step) => {
    if (!step || step > TOTAL_WIZARD_STEPS) {
      return `Step ${step}`;
    }
    const key = WIZARD_STEP_KEYS[step - 1];
    if (!key) {
      return `Step ${step}`;
    }
    return t(key, WIZARD_STEP_FALLBACK_LABELS[step - 1] || `Step ${step}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-900/20 text-yellow-400 border border-yellow-800',
      contacted: 'bg-green-900/20 text-green-400 border border-green-800',
      converted: 'bg-blue-900/20 text-blue-400 border border-blue-800',
      none: 'bg-gray-700 text-gray-400 border border-gray-600'
    };
    return colors[status] || 'bg-gray-700 text-gray-400 border border-gray-600';
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const duration = Math.round((end - start) / 60000); // minutes
    return `${duration} min`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Wizard Exit Tracking</h2>
        <p className="mt-1 text-sm text-gray-400">
          Monitor user journey patterns and manage follow-up actions
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-white">{sessions.length}</div>
          <div className="text-sm text-gray-400">Total Sessions</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-red-400">{sessions.filter(s => s.exit_intent_id).length}</div>
          <div className="text-sm text-gray-400">Exits</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-yellow-400">{sessions.filter(s => s.follow_up_status === 'pending').length}</div>
          <div className="text-sm text-gray-400">Pending Follow-up</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-2xl font-bold text-green-400">{sessions.filter(s => s.follow_up_status === 'contacted').length}</div>
          <div className="text-sm text-gray-400">Contacted</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-8">
          {[
            { key: 'sessions', label: 'Sessions' },
            { key: 'intents', label: 'Exit Intents' },
            { key: 'analytics', label: 'Analytics' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className="bg-gray-800 rounded-lg shadow border border-gray-700">
          {/* Filters */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex flex-wrap gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="none">No Follow-up</option>
              </select>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="today">Today</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>

          {/* Sessions Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Exit Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {session.session_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {session.country_selection?.selectedCountry || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">
                        {getStepName(session.current_step)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Step {session.current_step} of {session.total_steps}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {formatDuration(session.start_time, session.end_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">{session.exit_reason}</div>
                      <div className="text-xs text-gray-500">{session.wizard_exit_intents?.intent_label || session.exit_intent}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {session.contact_email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(session.follow_up_status)}`}>
                        {session.follow_up_status || 'none'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Exit Intents Tab */}
      {activeTab === 'intents' && (
        <div className="bg-gray-800 rounded-lg shadow border border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Intent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Conversion Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Follow-up Required
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {exitIntents.map((intent) => {
                  const sessionCount = sessions.filter(s => s.exit_intent_id === intent.id).length;
                  const convertedCount = sessions.filter(s => s.exit_intent_id === intent.id && s.follow_up_status === 'contacted').length;
                  const conversionRate = sessionCount > 0 ? convertedCount / sessionCount : 0;

                  return (
                    <tr key={intent.id} className="hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{intent.intent_label}</div>
                        <div className="text-xs text-gray-500">{intent.intent_key}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {intent.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {sessionCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{(conversionRate * 100).toFixed(1)}%</div>
                        <div className="w-16 bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${conversionRate * 100}%` }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {intent.followup_required ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-900/20 text-green-400 border border-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-700 text-gray-400 border border-gray-600">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button className="text-blue-400 hover:text-blue-300 mr-3">Edit</button>
                        <button className="text-gray-400 hover:text-gray-200">View Sessions</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Exit by Step Chart */}
          <div className="bg-gray-800 p-6 rounded-lg shadow border border-gray-700">
            <h3 className="text-lg font-medium text-white mb-4">Exit Points Analysis</h3>
            <div className="space-y-3">
              {[
                { step: 1, name: 'Country Selection', exits: 8, percentage: 30 },
                { step: 3, name: 'Education', exits: 12, percentage: 45 },
                { step: 5, name: 'Language Skills', exits: 4, percentage: 15 },
                { step: 7, name: 'Review', exits: 3, percentage: 10 }
              ].map((item) => (
                <div key={item.step} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">{item.name}</div>
                    <div className="text-xs text-gray-500">Step {item.step}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-red-500 h-2 rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-400 w-12 text-right">{item.exits}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Exit Reasons */}
          <div className="bg-gray-800 p-6 rounded-lg shadow border border-gray-700">
            <h3 className="text-lg font-medium text-white mb-4">Top Exit Reasons</h3>
            <div className="space-y-3">
              {[
                { reason: 'Too Expensive', count: 15, color: 'bg-red-500' },
                { reason: 'Need More Information', count: 12, color: 'bg-yellow-500' },
                { reason: 'Just Browsing', count: 8, color: 'bg-gray-500' },
                { reason: 'Not Ready Right Now', count: 6, color: 'bg-blue-500' },
                { reason: 'Technical Difficulty', count: 3, color: 'bg-orange-500' }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                    <span className="text-sm font-medium text-white">{item.reason}</span>
                  </div>
                  <span className="text-sm text-gray-400">{item.count} exits</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WizardExitManager;
